import { useEffect, useState } from 'react';

const STORAGE_KEY = 'cairn.privacyBannerDismissed';

/**
 * One-line reassurance for data-entry pages: "your data stays here".
 * Dismissable; preference is persisted to localStorage so the user only
 * sees it until they've internalized the message. Click "Read more"
 * navigates to the dedicated Privacy section.
 *
 * Tone target: subtle, friendly, not alarming. The pill in the top bar
 * carries the same message persistently for users who dismissed this.
 */
export function PrivacyBanner() {
  const [visible, setVisible] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== '1';
    } catch {
      return true;
    }
  });

  // Re-check on mount in case another tab dismissed; rare but cheap.
  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') setVisible(false);
    } catch {
      /* ignore */
    }
  }, []);

  if (!visible) return null;

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  }

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-xl border border-ink-200 bg-ink-50/60 px-3 py-2 text-[11px] text-ink-600 dark:border-ink-700 dark:bg-ink-800/40 dark:text-ink-300"
    >
      <span className="text-base leading-none" aria-hidden>
        🔒
      </span>
      <span className="flex-1">
        Your data stays on this device — Cairn makes zero network calls.{' '}
        <a
          href="#privacy"
          className="font-semibold text-accent-700 underline-offset-2 hover:underline dark:text-accent-500"
        >
          Read more →
        </a>
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss privacy banner"
        className="rounded p-1 text-ink-400 hover:bg-ink-200/50 hover:text-ink-700 dark:hover:bg-ink-700/40 dark:hover:text-ink-100"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
